
import {DateTime, Interval, Duration} from 'luxon';
import * as chrono from 'chrono-node';

function set_interval_in_year(intv, year) {
    const intv_start = intv.start.set({'year': year});
    return Interval.after(intv_start, intv.toDuration());
}

class CompoundInterval {
    constructor(...intvs) {
        this.intvs = intvs;
    }

    toRepr() {
        return 'CompoundInterval|' + this.intvs.map(intv => intv.toISO()).join(',')
    }

    static fromRepr(str) {
        const [intv_type, intv_str] = str.split('|')
        if (intv_type != 'CompoundInterval') {
            throw "Expected intv_type to be CompoundInterval, got '" + intv_type + "'";
        }
        const intvs = intv_str.split(',').map(istr => Interval.fromISO(istr, {'zone': 'utc'}));
        return new CompoundInterval(...intvs);
    }

    overlaps(other_intv) {
        return this.intvs.some(intv => intv.overlaps(other_intv))
    }

    engulfs(other_intv) {
        return this.intvs.some(intv => intv.engulfs(other_intv))
    }

    contains(dt) {
        return this.intvs.some(intv => intv.contains(dt))
    }
}

class SelectionInterval {
    constructor(intv, year_bnds) {
        if (year_bnds === undefined) {
            year_bnds = null;
        }

        this._intv = intv;
        this._year_bnds = year_bnds;
    }

    isYearly() {
        return this._year_bnds !== null;
    }

    setYearBounds(year_bnds) {
        return new SelectionInterval(this._intv, year_bnds);
    }

    getPaddedInterval() {
        const factor = 1.4;
        const half_dur = this.duration() / 2;
        const intv_center = this.start().plus(half_dur);
    
        let pad_intv_start = intv_center.minus(half_dur * factor);
        let pad_intv_end = intv_center.plus(half_dur * factor);

        return new SelectionInterval(Interval.fromDateTimes(pad_intv_start, pad_intv_end));
    }

    getDatetimes(dintv, cday_offset) {
        const subintvs = this.getSubintervals(dintv, cday_offset);
        const last_subintv = subintvs[subintvs.length - 1];

        return subintvs.map(i => i.start).concat([last_subintv.end]).filter(dt => this._intv.contains(dt));
    }

    getIntervals() {
        if (!this.isYearly()) {
            return [this._intv];
        }

        let intvs = [];
        for (let year = this._year_bnds[0]; year <= this._year_bnds[1]; year++) {
            const intv_start = this._intv.start.set({'year': year});
            const intv_end = this._intv.end.set({'year': year});
            const intv = Interval.fromDateTimes(intv_start, intv_end);
            intvs.push(intv);
        }
        return intvs;
    }

    getSubintervals(dintv, cday_offset) {
        // Make the interval start on a round number based on how big the subdivisions are.
        // For example, if the interval starts at 1134 UTC and we want to subidivide by hour, we'd make the
        //  interval start at 1100 UTC
    
        let interval_start = this._intv.start;
        let interval_end = this._intv.end;
    
        if (dintv < Duration.fromISO('PT1H')) {
            interval_start = interval_start.startOf('hour')
            interval_end = interval_end.startOf('hour').plus({'hours': 1});
        }
        else if (Duration.fromISO('PT1H') <= dintv && dintv < Duration.fromISO('PT24H')) {
            const round = dintv.shiftTo('milliseconds').milliseconds;
    
            interval_start = DateTime.fromMillis(Math.floor(interval_start.toMillis() / round) * round, {'zone': 'UTC'});
            interval_end = DateTime.fromMillis(Math.ceil(interval_end.toMillis() / round) * round, {'zone': 'UTC'});
        }
        else if (Duration.fromISO('P1D') <= dintv && dintv < Duration.fromISO('P21D')) {
            interval_start = interval_start.minus(cday_offset).startOf('day').plus(cday_offset);
            interval_end = interval_end.startOf('day').plus({'days': 1}).plus(cday_offset);
        }
        else if (Duration.fromISO('P21D') <= dintv && dintv < Duration.fromISO('P1Y')) {
            interval_start = interval_start.startOf('month').plus(cday_offset);
            interval_end = interval_end.startOf('month').plus({'months': 1}).plus(cday_offset);
        }
        else if (Duration.fromISO('P1Y') <= dintv && dintv < Duration.fromISO('P10Y')) {
            interval_start = interval_start.startOf('year').plus(cday_offset);
            interval_end = interval_end.startOf('year').plus({'years': 1}).plus(cday_offset);
        }
        else if (Duration.fromISO('P10Y') <= dintv) {
            interval_start = interval_start.startOf('year').plus(cday_offset);
            interval_end = interval_end.startOf('year').plus(cday_offset);
    
            interval_start = interval_start.minus({'year': interval_start.year % 10});
            interval_end = interval_end.plus({'years': 10 - (interval_end.year % 10)});
        }
    
        let intvs = Interval.fromDateTimes(interval_start, interval_end).splitBy(dintv);
        
        if (!this.isYearly()) {
            return intvs;
        }

        return intvs.map(sintv => this.intersection(sintv));
    }

    toString(cday_offset, data_limits, with_preposition) {
        if (with_preposition === undefined) {
            with_preposition = false;
        }
        let start_parts = this._intv.start.minus(cday_offset).toLocaleParts(DateTime.DATE_MED);
        let end_parts = this._intv.end.minus(cday_offset).minus({'millisecond': 1}).toLocaleParts(DateTime.DATE_MED);

        if (this.isYearly()) {
            start_parts = start_parts.slice(0, 3);
            end_parts = end_parts.slice(0, 3);
        }

        let intv_string;
        let preposition = 'from';

        if (!this.isYearly() && start_parts.filter(p => p['type'] == 'year')[0]['value'] != end_parts.filter(p => p['type'] == 'year')[0]['value']) {
            // None of (day, month, year) are the same
            intv_string = start_parts.map(p => p['value']).join('') + '-' + end_parts.map(p => p['value']).join('');
        }
        else if (start_parts.filter(p => p['type'] == 'month')[0]['value'] != end_parts.filter(p => p['type'] == 'month')[0]['value']) {
            // Year is the same, but day and month aren't
            intv_string = start_parts.slice(0, 3).map(p => p['value']).join('') + '-' + end_parts.map(p => p['value']).join('');
        }
        else if (start_parts.filter(p => p['type'] == 'day')[0]['value'] != end_parts.filter(p => p['type'] == 'day')[0]['value']) {
            // Year and month are the same, but the day isn't
            intv_string = start_parts.slice(0, 3).map(p => p['value']).join('') + '-' + end_parts.slice(2).map(p => p['value']).join('');
        }
        else {
            // Start and end have the same (day, month, year)
            intv_string = start_parts.map(p => p['value']).join('');
            preposition = 'on';
        }
        
        if (this.isYearly() && (this._year_bnds[0] != data_limits.start.year || this._year_bnds[1] != data_limits.end.year - 1)) {
            intv_string += (', ' + this._year_bnds[0] + '-' + this._year_bnds[1]);
        }

        if (with_preposition) {
            return {'preposition': preposition, 'string': intv_string};
        }
        return intv_string;
    }

    toURLFragment() {
        const start = this._intv.start;
        const end = this._intv.end;

        if (this.isYearly()) {
            return start.toFormat('MM-dd') + ';' + end.minus({'days': 1}).toFormat('MM-dd');
        }

        return start.toFormat("yyyy-MM-dd'T'HH:mm'Z'") + ';' + end.toFormat("yyyy-MM-dd'T'HH:mm'Z'")
    }

    static fromURLFragment(frag_str, cday_offset, data_limits) {
        const [start_str, end_str] = frag_str.split(';');
        let start, end, year_bnds;
        if (start_str.includes('T')) {
            start = DateTime.fromISO(start_str, {'zone': 'utc'});
            end = DateTime.fromISO(end_str, {'zone': 'utc'});
            year_bnds = null;
        }
        else {
            start = DateTime.fromFormat(start_str, 'MM-dd', {'zone': 'utc'}).set({'year': 2019}).plus(cday_offset);
            end = DateTime.fromFormat(end_str, 'MM-dd', {'zone': 'utc'}).set({'year': 2019}).plus(cday_offset).plus({'days': 1});
            year_bnds = [data_limits.start.year, data_limits.end.year - 1];
        }

        const intv = new SelectionInterval(Interval.fromDateTimes(start, end), year_bnds);
        return intv;
    }

    static fromString(date_str, cday_offset, data_limits) {
        date_str = date_str.replace(/spring/i, 'march-may');
        date_str = date_str.replace(/summer/i, 'june-august');
        date_str = date_str.replace(/fall/i, 'september-november');
        date_str = date_str.replace(/autumn/i, 'september-november');
        if (date_str.match(/winter/i)) {
            let year_match = date_str.match(/winter\s*((?:18|19|20)[\d]{2})/i);

            if (year_match !== null) {
                let year = year_match[1];
                date_str = 'december ' + (year - 1) + '-february ' + year;
            }
            else {
                date_str = date_str.replace(/winter/i, 'december-february');
            }
        }

        // Handle ranges of years correctly
        date_str = date_str.replace(/([\d]{4})([\s]*(?:-|to)[\s]*)([\d]{4})/i, 'jan 1, $1$2dec 31, $3');

        // chrono doesn't get month abbreviations unless they're followed by a space?
        date_str += " ";

        if (date_str.match(/[\D]+[\s]*-[\s]*[\D]+/)) {
            date_str = date_str.replace(' - ', '-');
        }
        else {
            date_str = date_str.replace('-', ' - ');
        }

        let date_parsed = chrono.parse(date_str);

        let search_start, search_end, intv_string, is_range = false, is_yearly = false;

        var parse_known_vals = kv => {
            const valid = ['year', 'month', 'day', 'hour', 'minute', 'second', 'millisecond'];

            kv = Object.fromEntries(Object.entries(kv).filter(([k, v]) => valid.includes(k)));
            kv['zone'] = 'UTC';

            return kv;
        }

        if (date_parsed.length == 0) {
            date_str = date_str.trim();
            if (date_str.match(/^(?:17|18|19|20)[\d]{2}$/)) {
                let year = parseInt(date_str)
                search_start = DateTime.fromObject({'year': parseInt(year), 'month': 1, 'day': 1, 'hour': 0, 'zone': 'utc'});
                search_end = search_start.plus({'years': 1});
            }
            else if (date_str.match(/^(?:18|19|20)[\d]{2}-(?:18|19|20)[\d]{2}$/)) {
                let [start_year, end_year] = date_str.split('-');
                search_start = DateTime.fromObject({'year': parseInt(start_year), 'month': 1, 'day': 1, 'hour': 0, 'zone': 'utc'});
                search_end = DateTime.fromObject({'year': parseInt(end_year), 'month': 1, 'day': 1, 'hour': 0}).plus({'years': 1});
            }
            else {
                //console.log("couldn't parse string: '" + date_str + "'");
                return null;
            }
        }
        else {
            let known_vals_start = parse_known_vals(date_parsed[0].start.knownValues);
            let known_vals_end = {};

            search_start = DateTime.fromObject(known_vals_start).set({'hours': 0, 'minute': 0, 'second': 0, 'millisecond': 0});

            date_parsed = chrono.parse(date_str, search_start.toJSDate());

            if (date_parsed.length == 0) {
                return null;
            }

            known_vals_start = parse_known_vals(date_parsed[0].start.knownValues);
            search_start = DateTime.fromObject(known_vals_start).set({'hours': 0, 'minute': 0, 'second': 0, 'millisecond': 0});

            if (known_vals_start['year'] === undefined) {
                search_start = search_start.set({'year': 2019});
            }

            if (date_parsed[0].end !== null) {
                known_vals_end = parse_known_vals(date_parsed[0].end.knownValues);

                search_end = DateTime.fromObject(known_vals_end).set({'hours': 0, 'minute': 0, 'second': 0, 'millisecond': 0});
                if (known_vals_end['year'] === undefined) {
                    search_end = search_end.set({'year': 2019});
                }

                if (search_end < search_start && known_vals_start['year'] === undefined) {
                    search_start = search_start.minus({'year': 1});
                }

                let offset;
                if (known_vals_end['day'] !== undefined) {
                    offset = {'days': 1};
                }
                else {
                    offset = {'months': 1};
                }
                search_end = search_end.plus(offset);

                is_range = true;
            }
            else {
                let offset;
                if (known_vals_start['day'] !== undefined) {
                    offset = {'days': 1};
                }
                else {
                    offset = {'months': 1};
                    is_range = true;
                }
                search_end = search_start.plus(offset);
            }

            if (known_vals_start['year'] === undefined && known_vals_end['year'] === undefined) {
                is_yearly = true;
            }
        }

        let new_intv = Interval.fromDateTimes(search_start.plus(cday_offset), search_end.plus(cday_offset));
        new_intv = new_intv.intersection(data_limits);

        if (new_intv === null) {
            return null;
        }

        let data_bnds = null;

        if (is_yearly) {
            data_bnds = [data_limits.start.year, data_limits.end.year - 1];
        }

        return new SelectionInterval(new_intv, data_bnds);
    }

    toRepr() {
        const year_bnds_str = this._year_bnds === null ? 'null' : this._year_bnds.join(';');
        return 'SelectionInterval|' + this._intv.toISO() + ',' + year_bnds_str;
    }

    static fromRepr(str) {
        const [intv_type, intv_str] = str.split('|')
        if (intv_type != 'SelectionInterval') {
            throw "Expected intv_type to be SelectionInterval, got '" + intv_type + "'";
        }
        const [intv_intv_str, year_bnds_str] = intv_str.split(',');
        const intv = Interval.fromISO(intv_intv_str);
        const year_bnds = year_bnds_str == 'null' ? null : year_bnds_str.split(';').map(parseInt);
        return new SelectionInterval(intv, year_bnds);
    }

    start() {
        if (!this.isYearly()) {
            return this._intv.start;
        }
        return this._intv.start.set({'year': this._year_bnds[0]}).startOf('year');
    }

    end() {
        if (!this.isYearly()) {
            return this._intv.end;
        }
        return this._intv.end.set({'year': this._year_bnds[1]}).endOf('year');
    }

    duration(single_year) {
        if (single_year === undefined) {
            single_year = false;
        }

        if (!this.isYearly() || single_year) {
            return this._intv.toDuration();
        }
        return this.end().diff(this.start());
    }

    equals(other) {
        let bnds_equal;
        if (this._year_bnds === null && other._year_bnds === null) {
            bnds_equal = true;
        }
        else if (this._year_bnds !== null && other._year_bnds === null 
              || this._year_bnds === null && other._year_bnds !== null) {
            bnds_equal = false;
        }
        else {
            bnds_equal = (this._year_bnds[0] == other._year_bnds[0]
                       && this._year_bnds[1] == other._year_bnds[1]);
        }
        return this._intv.equals(other._intv) && bnds_equal;
    }

    overlaps(intv) {
        if (!this.isYearly()) {
            return this._intv.overlaps(intv);
        }

        if (intv.end.year < this._year_bnds[0] || intv.start.year > this._year_bnds[1]) {
            return false;
        }

        if (intv.toDuration() > Duration.fromISO('P1Y')) {
            return true;
        }

        const intv_yr_start = set_interval_in_year(this._intv, intv.end.year - 1);
        const intv_yr_end = set_interval_in_year(this._intv, intv.end.year);

        return intv_yr_start.overlaps(intv) || intv_yr_end.overlaps(intv);
    }

    engulfs(intv) {
        if (!this.isYearly()) {
            return this._intv.engulfs(intv);
        }

        if (intv.start.year < this._year_bnds[0] || intv.end.year < this._year_bnds[0] 
            || intv.start.year > this._year_bnds[1] || intv.end.year > this._year_bnds[1]) {
            return false;
        }

        const intv_yr_start = set_interval_in_year(this._intv, intv.end.year - 1);
        const intv_yr_end = set_interval_in_year(this._intv, intv.end.year);

        return intv_yr_start.engulfs(intv) || intv_yr_end.engulfs(intv);
    }

    contains(dt) {
        if (!this.isYearly()) {
            return this._intv.contains(dt);
        }

        if (dt.year < this._year_bnds[0] || dt.year > this._year_bnds[1]) {
            return false;
        }

        const intv_yr_start = set_interval_in_year(this._intv, dt.year - 1);
        const intv_yr_end = set_interval_in_year(this._intv, dt.year);

        return intv_yr_start.contains(dt) || intv_yr_end.contains(dt);
    }

    intersection(intv, month_only) {
        if (month_only === undefined) {
            month_only = false;
        }

        if (this.isYearly() && !month_only && (intv.end.year < this._year_bnds[0] || intv.start.year > this._year_bnds[1])) {
            return null;
        }

        const intv_yr_sintv = set_interval_in_year(this._intv, intv.start.year);
        let intersects = [intv_yr_sintv.intersection(intv)];

        if (this._intv.start.year != this._intv.end.year) {
            const intv_yr_sintv_m1 = set_interval_in_year(this._intv, intv.start.year - 1);
            intersects.push(intv_yr_sintv_m1.intersection(intv));
        }

        intersects = intersects.filter(intv => intv !== null);

        if (intersects.length == 0) {
            return null;
        }
        else if (intersects.length == 1) {
            return intersects[0];
        }
        else {
            return new CompoundInterval(...intersects);
        } 
    }
}

export {SelectionInterval, CompoundInterval};